import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { COMPANY, NAV_LINKS, SOCIALS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.2v12.93a2.59 2.59 0 1 1-2.59-2.59c.27 0 .52.04.77.11V8.2a5.78 5.78 0 0 0-.77-.05A5.79 5.79 0 1 0 15.34 14V8.86a7.45 7.45 0 0 0 4.37 1.4V7.06a4.28 4.28 0 0 1-3.11-1.24Z" />
  </svg>
);

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, tiktok: TikTokIcon };

export const Footer = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const trackSocial = (platform) => {
    axios.post(`${API}/track/social-click`, { platform }).catch(() => {});
  };

  return (
    <footer data-testid="main-footer" className="relative border-t border-white/10 bg-[#050505] overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
              <span className="font-display font-extrabold text-xl">AutoHire <span className="text-gold-gradient">STL</span></span>
            </div>
            <p className="text-white/50 mt-4 max-w-sm font-light">
              Premium car rentals in St. Louis. Clean fleet, transparent pricing, delivered to your door. {COMPANY.est}
            </p>
            <p className="font-script italic text-2xl text-[#F2B705] mt-4">"{COMPANY.tagline}"</p>
            <div className="flex gap-3 mt-6">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.id] || Instagram;
                return (
                  <a key={s.id} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-social-${s.id}`}
                    onClick={() => trackSocial(s.id)}
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#F2B705] hover:text-black hover:border-[#F2B705] transition-colors">
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-widest text-white/40 mb-4">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button onClick={() => scrollTo(l.href)} className="text-white/70 hover:text-[#F2B705] transition-colors">{l.label}</button>
                </li>
              ))}
              <li>
                <Link to="/terms" data-testid="footer-terms-link" className="text-white/70 hover:text-[#F2B705] transition-colors">Terms &amp; Conditions</Link>
              </li>
              <li>
                <Link to="/privacy" data-testid="footer-privacy-link" className="text-white/70 hover:text-[#F2B705] transition-colors">Privacy Policy</Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-widest text-white/40 mb-4">Contact</h4>
            <ul className="space-y-3 text-white/70">
              <li><a href={COMPANY.phoneHref} className="flex items-center gap-2 hover:text-[#F2B705] transition-colors"><Phone size={16} className="text-[#F2B705]" /> {COMPANY.phone}</a></li>
              <li><a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-[#F2B705] transition-colors"><Mail size={16} className="text-[#F2B705]" /> {COMPANY.email}</a></li>
              <li className="flex items-center gap-2"><MapPin size={16} className="text-[#F2B705]" /> {COMPANY.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col gap-3 text-sm text-white/40">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-[#F2B705] transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-[#F2B705] transition-colors">Privacy</Link>
              <span>Ride With Us</span>
            </div>
          </div>
          <span className="text-white/30 text-xs text-center md:text-left">{COMPANY.legalName} · {COMPANY.address}</span>
        </div>
      </div>

      <div className="font-display font-black text-[18vw] leading-none text-white/[0.025] text-center select-none -mb-[3vw] pointer-events-none">
        AUTOHIRE
      </div>
    </footer>
  );
};
